import { blockPalette, blockTheme, ContentBlockStyles } from "@gaiaProfessionals/Widgets/HCP/ContentBlocks/HCPContentBlocks.styles";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import React, { FC } from "react";
import styled, { css } from "styled-components";

// <============== Fields Data ==============>
const enum fields {
    backgroundType = "backgroundType",
    bgColorMobile = "bgColorMobile",
    bgImage = "bgImage",
    mobileBackgroundColorOption = "mobileBackgroundColorOption",
    bgColor = "bgColor",
    bgImageMobile = "bgImageMobile",
    blockTheme = "blockTheme",
    blockTextAlign = "blockTextAlign",
    contentImage = "contentImage",
    contentImageAlt = "contentImageAlt",
    heroHeader = "heroHeader",
    heroSubHeader = "heroSubHeader",
    heroText = "heroText",
    btnLabel = "btnLabel",
    btnLink = "btnLink"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundType]: "image" | "color";
        [fields.bgColorMobile]: string;
        [fields.bgImage]: string;
        [fields.mobileBackgroundColorOption]: boolean;
        [fields.bgColor]: string;
        [fields.bgImageMobile]: string;
        [fields.blockTheme]: "light" | "dark";
        [fields.blockTextAlign]: "right" | "left";
        [fields.contentImage]: string;
        [fields.contentImageAlt]: string;
        [fields.heroHeader]: string;
        [fields.heroSubHeader]: string;
        [fields.heroText]: string;
        [fields.btnLabel]: string;
        [fields.btnLink]: LinkFieldValue;
    };
}

// <============== Hero Block Styled Component ==============>
interface HeroProps {
    fields: {
        [fields.backgroundType]: "image" | "color";
        [fields.bgColorMobile]: string;
        [fields.bgImage]: string;
        [fields.bgImageMobile]: string;
        [fields.bgColor]: string;
        [fields.blockTheme]: "light" | "dark";
        [fields.blockTextAlign]: "right" | "left";
    };
}

const HeroBlock = styled.div<HeroProps>`
    ${blockTheme.container}
    ${blockTheme.rowMargin}
    display: flex;
    flex-direction: column;
    ${props => props.fields.backgroundType === "image"
        ? css`
            background-image: url("${props.fields.bgImageMobile}");
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            background-color: ${props.fields.bgColorMobile};`
        : css`
            background-color: ${props.fields.bgColor};`
    }
    border-radius: 15px;
    // Media Queries
    /* Medium Devices, Desktops */
    @media screen and (min-width: 992px) {
        min-height: 423px;
        flex-direction: ${props => props.fields.blockTextAlign === "right" ? "row-reverse" : "row"};
        ${props => props.fields.backgroundType === "image"
            ? css`
                background-image: url("${props.fields.bgImage}");`
            : css`
                background-color: ${props.fields.bgColor};`
        }
    }
    /* Large Devices, Wide Screens */
    @media screen and (min-width: 1200px) {
        min-height: 623px;
    }

    /* =============== Text Block =============== */
    .textBlock {
        width: 100%;
        padding: 2rem 5%;
        display: flex;
        align-items: center;
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            width: 45%;
            padding: 2rem;
            padding-right: ${props => props.fields.blockTextAlign === "right" ? "5%" : "1.75%"};
            padding-left: ${props => props.fields.blockTextAlign === "right" ? "1.75%" : "5%"};
        }
        /* Large Devices, Wide Screens */
        @media screen and (min-width: 1200px) {
            width: 33.333%;
        }
    }
    .textBlock__wrap {
        width: 100%;
    }
    .textBlock__head {
        font-size: 2rem;
        line-height: 1.15;
        margin-bottom: .5rem;
        ${props => props.fields.blockTheme === "light"
            ? css`
                color: ${blockPalette.white};`
            : css`
                color: ${blockPalette.green};`
        }
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            font-size: 2rem;
        }
        /* Large Devices */
        @media screen and (min-width: 1200px) {
            font-size: 2.5rem;
        }
        /* Largest Devices, Wide Screens */
        @media screen and (min-width: 1400px) {
            font-size: 3rem;
        }
    }
    .textBlock__subHead {
        ${blockTheme.pTypography}
        font-size: 1.25rem;
        font-weight: 600;
        ${props => props.fields.blockTheme === "light"
            ? css`
                color: ${blockPalette.white};`
            : css`
                color: ${blockPalette.gold};`
        }
        text-transform: uppercase;
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            font-size: 1.5rem;
        }
    }
    .textBlock__text {
        ${props => props.fields.blockTheme === "light"
            ? css`
                color: ${blockPalette.white};`
            : css`
                color: ${blockPalette.green};`
        }
    }
    .textBlock__btn {
        margin-top: 2.5rem;
    }

    /* =============== Image Block =============== */
    .imgBlock {
        width: 100%;
            display: flex;
        align-items: center;
        justify-content: center;
        // Media Queries
        /* Medium Devices, Desktops */
        @media screen and (min-width: 992px) {
            width: 55%;
        }
        /* Large Devices, Wide Screens */
        @media screen and (min-width: 1200px) {
            width: 66.666%;
        }
    }
`;

// <============== Component ==============>
const HCPHero: FC<OwnProps> = ({ fields }) => {

    const { url } = useGetLink(fields.btnLink);

    return (
        <ContentBlockStyles>
            <HeroBlock fields={fields}>
                <div className="textBlock">
                    <div className="textBlock__wrap">
                        {fields.heroHeader && <h2 className="textBlock__head">{fields.heroHeader}</h2>}
                        {fields.heroSubHeader && <h3 className="textBlock__subHead">{fields.heroSubHeader}</h3>}
                        {fields.heroText && <p className="textBlock__text">{fields.heroText}</p>}
                        {url && (
                            <Link className="btn btn--primary textBlock__btn" href={url}>
                                {fields.btnLabel}
                            </Link>
                        )}
                    </div>
                </div>
                {fields.contentImage && (
                    <div className="imgBlock">
                        <img src={fields.contentImage} alt={fields.contentImageAlt} />
                    </div>
                )}
            </HeroBlock>
        </ContentBlockStyles>
    );
};

// <============== Widget Fields ==============>
const styleTab = {
    displayName: "Style",
    sortOrder: 0,
};
const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const HCPHeroBlock: WidgetModule = {
    component: HCPHero,
    definition: {
        group: "Common",
        icon: "Banner",
        displayName: "HCP Hero Block",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.backgroundType,
                displayName: "Background Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" }
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: styleTab,
            },
            {
                fieldType: "Translatable",
                name: fields.bgImage,
                displayName: "Background Image - Desktop",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.backgroundType === "image",
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.mobileBackgroundColorOption,
                displayName: "Swap Mobile Image for Background Color",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: widget => widget.fields.backgroundType === "image",
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.bgColorMobile,
                displayName: "Mobile Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.backgroundType === "image" && widget.fields.mobileBackgroundColorOption === true,
                tab: styleTab,
            },
            {
                fieldType: "Translatable",
                name: fields.bgImageMobile,
                displayName: "Background Image - Mobile",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.backgroundType === "image" && widget.fields.mobileBackgroundColorOption === false,
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.backgroundType === "color",
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.blockTheme,
                displayName: "Text Theme",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" }
                ],
                defaultValue: "dark",
                hideEmptyOption: true,
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.blockTextAlign,
                displayName: "Text Align",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Right", value: "right" },
                    { displayName: "Left", value: "left" }
                ],
                defaultValue: "right",
                hideEmptyOption: true,
                tab: styleTab,
            },
            {
                fieldType: "General",
                name: fields.heroHeader,
                displayName: "Header Text",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.heroSubHeader,
                displayName: "Sub Header Text",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.heroText,
                displayName: "Paragraph Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "Translatable",
                name: fields.btnLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Learn More",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.btnLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: contentTab,
            },
            {
                fieldType: "Translatable",
                name: fields.contentImage,
                displayName: "Content Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.contentImageAlt,
                displayName: "Content Image Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: contentTab,
            },
        ],
    },
};

export default HCPHeroBlock;
